import { environmentHttpClient as httpClient } from "client/Api/httpClient";
import {
	HubCommentModel,
	HubCommentPagingInformationModel,
	HubCommentSaveModel,
	HubCommentStatisticsModel,
	ReactionCounterModel,
	ReactionKind,
	ReactionUserModel
} from "../common";
import { SortDirection } from "client/Api/common";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";

export function getCommentsList(
	knowledgeItemId: number,
	amount: number | null,
	afterComment: HubCommentPagingInformationModel | null,
	sortDirection: SortDirection,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<HubCommentModel>> {
	return httpClient.get<LazyLoadPageResult<HubCommentModel>>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments`,
		{
			amount,
			afterComment,
			sortDirection
		},
		{ signal }
	);
}

export function getRelatedCommentThread(
	knowledgeItemId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<readonly HubCommentModel[]> {
	return httpClient.get<readonly HubCommentModel[]>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${commentId}/Thread`,
		undefined,
		{ signal }
	);
}

export function createComment(
	knowledgeItemId: number,
	newComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.post<HubCommentModel>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/`,
		newComment,
		{ signal }
	);
}

export function editComment(
	knowledgeItemId: number,
	commentId: number,
	updatedComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.put<HubCommentModel>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${commentId}`,
		updatedComment,
		{ signal }
	);
}

export function deleteComment(
	knowledgeItemId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<void> {
	return httpClient.delete<void>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${commentId}`,
		undefined,
		{ signal }
	);
}

export function createReply(
	knowledgeItemId: number,
	parentCommentId: number,
	newComment: HubCommentSaveModel,
	signal?: AbortSignal
): Promise<HubCommentModel> {
	return httpClient.post<HubCommentModel>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${parentCommentId}/Replies`,
		newComment,
		{ signal }
	);
}

export function registerCommentReaction(
	knowledgeItemId: number,
	commentId: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.post<HubCommentStatisticsModel>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${commentId}/Reactions`,
		kind,
		{ signal }
	);
}

export function updateCommentReaction(
	knowledgeItemId: number,
	commentId: number,
	kind: ReactionKind,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.put<HubCommentStatisticsModel>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${commentId}/Reactions`,
		kind,
		{ signal }
	);
}

export function unregisterCommentReaction(
	knowledgeItemId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<HubCommentStatisticsModel> {
	return httpClient.delete<HubCommentStatisticsModel>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${commentId}/Reactions`,
		undefined,
		{ signal }
	);
}

export function getCommentReactionCounts(
	knowledgeItemId: number,
	commentId: number,
	signal?: AbortSignal
): Promise<readonly ReactionCounterModel[]> {
	return httpClient.get<readonly ReactionCounterModel[]>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${commentId}/Reactions/Counts`,
		undefined,
		{ signal }
	);
}

export function getCommentReactions(
	knowledgeItemId: number,
	commentId: number,
	pageSize: number,
	skip: number,
	kind: ReactionKind | undefined,
	signal?: AbortSignal
): Promise<LazyLoadPageResult<ReactionUserModel>> {
	return httpClient.get<LazyLoadPageResult<ReactionUserModel>>(
		`/UiApi/KnowledgeItems/${knowledgeItemId}/Comments/${commentId}/Reactions`,
		{
			kind,
			pageSize,
			skip
		},
		{ signal }
	);
}

export function getKnowledgeItemCommentImageUrl(imageId: string): string {
	return createUrlRelativeToEnvironment(
		`/Hub/KnowledgeItems/Comments/Image/?imageId=${encodeURIComponent(imageId)}`
	);
}